import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const PRICING = "https://vizard.ai/pricing";
const HOME = "https://vizard.ai/";
const TECHSY = "https://techsy.io/en/blog/opusclip-vs-vizard";

export const VIZARD_CONTENT: CompareContent = {
  slug: "crayo-vs-vizard",
  competitorSlug: "vizard",
  title: "Crayo vs Vizard",
  seoTitle: "Crayo vs Vizard: Pricing, Metering and Moment Ranking",
  seoDescription:
    "Crayo vs Vizard with dated prices. Vizard meters per uploaded minute and has a free tier. Crayo ranks moments before you cut, charges per finished clip and puts a timeline editor on every plan.",
  cardVerdict: "Better than Crayo for free tier and 4K export, behind on moment ranking and metering.",
  bestFor: "Clippers weighing a free tier against per-clip pricing",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if you clip long podcasts and streams and want moments ranked before you cut, pay per finished clip rather than per uploaded minute, and want a timeline editor with no watermark on the ${money(hobby.monthly)} entry plan. Vizard is the better pick if you need a free tier, 4K export, 600-minute uploads or a scheduler to 20 accounts. The evidence is below.`,
  pickCrayoIf: [
    "You upload long sources and keep a few clips: a 60-minute episode that yields 8 clips costs 8 credits on Crayo and 60 on Vizard.",
    "You want moments ranked with the planner's reasons attached; a July 2026 reviewer found Vizard's selection closer to keyword tagging than a virality score.",
    "You want a face-aware camera track, 32 caption styles and no watermark on the entry plan: Vizard removes its watermark only on Creator.",
  ],
  pickThemIf: [
    "You need a free tier: Vizard's gives 60 credits a month with the full editor and API, at 720p with a watermark. Crayo has none.",
    "You export in 4K or upload sources longer than 3 hours: Vizard paid plans export 4K and accept 600-minute uploads. Crayo caps at 1080p and 3 hours.",
    "You schedule posts or work in a team: Creator schedules to 6 accounts, Business to 20 with a shared workspace. Crayo has neither.",
  ],
  wins: [
    {
      claim: "Crayo ranks moments by virality; Vizard publishes no virality score.",
      evidence:
        "Crayo's AutoClip and Find Viral Moments rank every candidate moment and attach the planner's findings so you review the list before cutting. Vizard's homepage and pricing page, checked Oct 8, 2026, describe clipping but no score; a July 2026 techsy.io comparison found there is no true virality score and described the selection as closer to basic keyword tagging.",
      href: TECHSY,
    },
    {
      claim: "Crayo meters by finished clip; Vizard meters by uploaded minute.",
      evidence:
        "Vizard's pricing page defines 1 credit as 1 minute of video, so a 60-minute podcast costs 60 credits however many clips you keep. Crayo charges 1 workflow credit per clip you generate, and Clip Cutter ranges are free.",
      href: PRICING,
    },
    {
      claim: "Crayo exports without a watermark on every plan; Vizard's Free plan watermarks at 720p.",
      evidence:
        `Vizard's Creator plan lists No watermark as an upgrade over Free, and Free exports top out at 720p with a 10-minute length limit. Crayo's cheapest plan, Hobby at ${money(hobby.monthly)} a month, exports at 1080p with no watermark.`,
      href: PRICING,
    },
    {
      claim: "Crayo includes View Tracker on every plan; Vizard's social accounts are for posting, not tracking.",
      evidence:
        "Crayo tracks Instagram, YouTube and TikTok creators for posting cadence and viral outliers, on 100 to 2,500 monthly tracker credits by plan; it is not post analytics. Vizard's pricing page counts social accounts only for its scheduler, 1 on Free, 6 on Creator, 20 on Business, and no analytics feature is visible on its homepage as of Oct 8, 2026.",
      href: PRICING,
    },
  ],
  concessions: [
    {
      claim: "Vizard has a real free tier with the full editor and API.",
      evidence:
        "60 credits a month, full video editor access and API access on Free, per the pricing page checked Oct 8, 2026, with a watermark, 720p export, 60-minute uploads and 3-day storage. Crayo has no free plan and no trial.",
      href: PRICING,
    },
    {
      claim: "Vizard exports 4K and accepts 600-minute uploads on paid plans.",
      evidence:
        "Creator and Business list Export videos in 4K and an upload length of 600 minutes. Crayo exports up to 1080p and takes sources up to 3 hours or 4 GB.",
      href: PRICING,
    },
    {
      claim: "Vizard schedules posts natively and has team workspaces.",
      evidence:
        "Creator schedules to 6 social accounts, Business to 20 with a shared workspace, brand kit and invited team members. Crayo has no scheduler and no team seats.",
      href: PRICING,
    },
    {
      claim: "Vizard translates captions into 100+ languages and ships 22 caption templates.",
      evidence:
        "The homepage says Translate your captions to over 100 languages and lists 22 templates in Vizard Agent. Crayo transcribes 56 spoken languages and has 32 caption styles, but does not translate captions.",
      href: HOME,
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Creator",
      cost: "$29/mo or $14.50/mo yearly ($174 billed yearly)",
      usage: "240 of 600 credits; Free's 60 credits would run out on the first source",
      note: "1 credit per uploaded minute, per the Vizard pricing page, read in a real browser on Oct 9, 2026 because the page renders prices client-side.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo",
    plans: "Vizard Creator and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Vizard's pricing page FAQ says a cancelled plan expires at the end of the billing cycle, the same as Crayo, but the page does not say where the cancel control lives. Refund terms are not on the pricing page. One linked review from March 2026 reports unused credits being forfeited on cancellation, while a third-party guide says paid-plan credits roll over as long as the plan stays active. Crayo's rule is simpler to state: credits reset monthly, no rollover, all sales final, cancel in one click from Settings.",
  faqs: [
    {
      question: "Is Crayo cheaper than Vizard?",
      answer:
        `For most clippers who upload long sources, yes, though not on the sticker. Vizard, often written Vizard AI, meters by the minute; Crayo meters by the clip. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} credits, and one credit is one finished clip. Vizard Creator is listed at $29 a month, or about $14.50 a month billed yearly, for 600 credits, and one credit is one uploaded minute, so the two entry prices match on monthly billing and Vizard is a couple of dollars a month lower on yearly billing. Four hour-long sources use 240 Vizard credits before a single clip is counted; the same month uses 32 Crayo credits. If you upload short sources and keep most of what the tool finds, Vizard's 600 minutes can stretch further, and its Free plan covers 60 minutes a month at 720p.`,
    },
    {
      question: "Does Crayo have a free plan like Vizard?",
      answer:
        `No. Crayo has no free plan and no trial. The only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload your file. The entry plan is Hobby at ${money(hobby.monthly)} a month. Vizard's Free plan gives 60 credits a month, which is 60 minutes of uploaded video, with the full editor and API, but exports carry a watermark, top out at 720p and 10 minutes, and storage lasts 3 days. If a free tier is the deciding factor, Vizard wins that row.`,
    },
    {
      question: "Does Vizard have a virality score like Crayo?",
      answer:
        "Not one that Vizard publishes. Its homepage and pricing page, checked Oct 8, 2026, describe one-click clipping and AI reframing but no per-moment score. A July 2026 comparison on techsy.io reported there is no true virality score and that Vizard's selection reads closer to basic keyword tagging. Crayo's AutoClip ranks every candidate moment and records the planner's reasons, and Find Viral Moments shows up to 50 ranked moments for review before any credit is spent on cutting. Our scheduled hands-on test on a two-person podcast will measure both.",
    },
    {
      question: "Is there a Vizard alternative without a watermark?",
      answer:
        `Crayo never adds a watermark, on any plan, at any resolution up to 1080p. Vizard removes its watermark on Creator, listed at $29 a month or about $14.50 a month billed yearly, and keeps it on Free. So the honest comparison is Vizard Creator against Crayo Hobby at ${money(hobby.monthly)} a month: Vizard's plan buys 600 uploaded minutes plus 4K and a scheduler; Crayo's buys ${hobby.credits} finished clips plus a timeline editor with a face-aware camera track, 32 caption styles and 56 spoken languages.`,
    },
    {
      question: "Does Crayo export 4K like Vizard?",
      answer:
        "No. Crayo exports up to 1080p on every plan, in 9:16, 1:1 and 4:5, with no watermark. Vizard Creator and Business export in 4K, which matters if you also publish horizontal cuts to YouTube at full resolution, while Vizard Free exports at 720p with a 10-minute length cap. Vizard paid plans also accept uploads up to 600 minutes against Crayo's 3 hours or 4 GB, so a 6-hour stream VOD needs splitting before it goes into Crayo. If 4K masters are part of your workflow, Vizard wins that row; if 1080p vertical is the whole job, the difference does not show on the phone.",
    },
    {
      question: "Does Crayo auto-post or support teams like Vizard?",
      answer:
        "No on both. Crayo exports the file and you post it yourself, and every plan is a single seat. Vizard Creator schedules posts to 6 social accounts, and Business schedules to 20 accounts with a shared workspace, a brand kit and invited team members at $10 a month per seat on monthly billing or $5 on yearly, read from the pricing page in a browser on Oct 9, 2026. If scheduling or a shared workspace is the deciding feature, Vizard wins that row.",
    },
    {
      question: "What happens to Vizard credits when you cancel?",
      answer:
        "Vizard's own pricing page FAQ says that once you cancel, the plan expires at the end of your billing cycle, which is the same rule Crayo applies. Refund terms are not on the pricing page. One dated review linked in the billing section reports that unused credits are forfeited at cancellation, and a third-party guide says paid-plan credits roll over while the plan stays active. Crayo's credits reset monthly with no rollover, all sales are final, and you cancel in one click from Settings.",
    },
    {
      question: "Vizard vs Opus Clip vs Crayo: what is the difference?",
      answer:
        "Vizard and Opus Clip both meter by uploaded minute: 1 credit per minute, 600 on Vizard Creator and 300 on Opus Clip Pro. Both have free tiers, schedulers and 4K on at least one paid plan. Crayo meters by finished clip, has no free tier, no scheduler and no 4K, and puts a timeline editor, 56 caption languages, a public API and View Tracker on every plan. Opus Clip prints a Virality Score per clip; Crayo ranks moments; Vizard publishes no score. Our Crayo vs Opus Clip page has the full Opus Clip breakdown.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Vizard prices read in a real browser Oct 9, 2026: Free 60 credits/mo at 720p with watermark, Creator $29/mo or $14.50/mo billed yearly ($174), Business $39/mo or $19.50/mo billed yearly ($234).",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-opus-clip", "crayo-vs-klap", "crayo-vs-capcut"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "vizard ai alternative",
    "vizard vs opus clip",
    "vizard ai vs crayo",
    "vizard ai pricing",
    "vizard alternative without watermark",
    "crayo vs vizard",
  ],
};
